import { CanMatchFn, Routes, UrlSegment } from '@angular/router';
import { environment } from '../environments/environment';
import { LANGS } from './seo/seo.config';
import { blogListResolver, blogPostResolver } from './services/blog.resolvers';
import { priceListResolver } from './services/price-list.resolver';

/**
 * Guards the `:lang` subtree so it only accepts a language we actually serve.
 *
 * `:lang` matches ANY single segment, so without this `/pippo` rendered the home page
 * at a made-up URL (200, indexable, duplicate of the real home) and `/pippo/qualsiasi`
 * rendered the 404 under a language that does not exist. Failing the match here lets
 * both fall through to the catch-all and answer 404 once.
 */
const isSupportedLang: CanMatchFn = (_route, segments: UrlSegment[]) =>
  segments.length > 0 && (LANGS as readonly string[]).includes(segments[0].path);

/**
 * Every page is code-split (`loadComponent`) rather than imported eagerly.
 *
 * With static imports the initial bundle carried ALL 14 pages — privacy, terms and
 * the checkout wizard alone are ~115 KB of source nobody hits from the landing page —
 * and Lighthouse measured 464 KB of unused JS plus 650 ms of blocking time on the
 * home route. SSR is unaffected: the server renders the matched route, and Angular
 * emits a `modulepreload` for that route's chunk, so it downloads in parallel with
 * `main.js` instead of after it.
 *
 * The `() => import(...)` is written INSIDE each route on purpose. Angular's build only
 * recognises an inline import there: with shared `const Page = () => import(...)` thunks
 * (as it was until October 2026) no page got its modulepreload, and its chunk started
 * downloading only after main.js had run (Lighthouse). The IT and EN slugs of a page still
 * import the same file, so they resolve to the SAME chunk (one request, one module).
 */

/**
 * Subscription CHECKOUT, IT + EN paths. Registered only when the feature is enabled
 * (dev/staging). In production it is omitted, so any direct hit on these URLs falls
 * through to the `**` route and lands on the 404 page.
 *
 * The pricing page is deliberately NOT in here any more: it is a marketing page with
 * fixed plan cards, it must be indexable and linked from the menu on every tier, and it
 * no longer depends on the plan catalogue being reachable. Only the wizard is gated.
 */
const subscriptionRoutes: Routes = environment.features.subscriptions
  ? [
      {
        path: 'abbonamento',
        loadComponent: () => import('./pages/checkout.component').then((m) => m.CheckoutPage),
        data: { seo: 'checkout' },
        resolve: { priceList: priceListResolver },
      },
      {
        path: 'checkout',
        loadComponent: () => import('./pages/checkout.component').then((m) => m.CheckoutPage),
        data: { seo: 'checkout' },
        resolve: { priceList: priceListResolver },
      },
    ]
  : [];

/**
 * Delete account (IT: elimina-account · EN: delete-account) — the page the Android app's
 * profile opens at `/it/elimina-account`, and the one its Google Play listing names.
 * Registered only when `features.deleteAccountPage` is on (dev/staging): in production its
 * text still has fields to decide, so the URLs fall through to the 404 page.
 */
const deleteAccountRoutes: Routes = environment.features.deleteAccountPage
  ? [
      {
        path: 'elimina-account',
        loadComponent: () =>
          import('./pages/delete-account.component').then((m) => m.DeleteAccount),
        data: { seo: 'deleteAccount' },
      },
      {
        path: 'delete-account',
        loadComponent: () =>
          import('./pages/delete-account.component').then((m) => m.DeleteAccount),
        data: { seo: 'deleteAccount' },
      },
    ]
  : [];

/**
 * Routing is language-prefixed (`/:lang/...`). Each page is registered under BOTH
 * its Italian (design) path and its English (spec) path, all resolving to the same
 * standalone component — so every design link and every spec route works.
 *
 * `data.seo` names the page in `seo/seo.config.ts`, which owns the ONE canonical
 * slug per language. SeoService reads it to emit title/description/canonical/hreflang
 * and the JSON-LD graph, so the duplicate slugs below collapse onto a single indexed
 * URL per language instead of competing with each other.
 */
export const routes: Routes = [
  { path: '', redirectTo: 'it', pathMatch: 'full' },
  {
    path: ':lang',
    canMatch: [isSupportedLang],
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/home.component').then((m) => m.HomePage),
        data: { seo: 'home' },
      },

      // Channels (IT: canali · EN: channels)
      {
        path: 'canali',
        loadComponent: () => import('./pages/channels.component').then((m) => m.Channels),
        data: { seo: 'channels' },
      },
      {
        path: 'channels',
        loadComponent: () => import('./pages/channels.component').then((m) => m.Channels),
        data: { seo: 'channels' },
      },

      // How it works (IT: come-funziona · EN: how-it-works)
      {
        path: 'come-funziona',
        loadComponent: () => import('./pages/how-it-works.component').then((m) => m.HowItWorks),
        data: { seo: 'howItWorks' },
      },
      {
        path: 'how-it-works',
        loadComponent: () => import('./pages/how-it-works.component').then((m) => m.HowItWorks),
        data: { seo: 'howItWorks' },
      },

      // Pricing (IT: prezzi · EN: pricing) — a marketing page, always registered.
      {
        path: 'prezzi',
        loadComponent: () => import('./pages/pricing.component').then((m) => m.Pricing),
        data: { seo: 'pricing' },
        resolve: { priceList: priceListResolver },
      },
      {
        path: 'pricing',
        loadComponent: () => import('./pages/pricing.component').then((m) => m.Pricing),
        data: { seo: 'pricing' },
        resolve: { priceList: priceListResolver },
      },

      // Features  (IT: funzioni · EN/spec: features, services)
      {
        path: 'funzioni',
        loadComponent: () => import('./pages/features.component').then((m) => m.Features),
        data: { seo: 'features' },
      },
      {
        path: 'features',
        loadComponent: () => import('./pages/features.component').then((m) => m.Features),
        data: { seo: 'features' },
      },
      {
        path: 'services',
        loadComponent: () => import('./pages/features.component').then((m) => m.Features),
        data: { seo: 'features' },
      },
      {
        path: 'services/:id',
        loadComponent: () =>
          import('./pages/feature.main.component').then((m) => m.FeaturesMainComponent),
        data: { seo: 'useCaseDetail' },
      },

      // Firma e pratiche (IT: firma-e-pratiche · EN: signature-and-documents) — one page for
      // the graphometric signature on the pad and the dossiers it closes. The old
      // addresses of the two separate pages send people here.
      {
        path: 'firma-e-pratiche',
        loadComponent: () => import('./pages/sign.component').then((m) => m.SignPage),
        data: { seo: 'sign' },
      },
      {
        path: 'signature-and-documents',
        loadComponent: () => import('./pages/sign.component').then((m) => m.SignPage),
        data: { seo: 'sign' },
      },
      { path: 'firma', redirectTo: 'firma-e-pratiche', pathMatch: 'full' },
      { path: 'pratiche', redirectTo: 'firma-e-pratiche', pathMatch: 'full' },
      { path: 'sign', redirectTo: 'signature-and-documents', pathMatch: 'full' },
      { path: 'documents', redirectTo: 'signature-and-documents', pathMatch: 'full' },

      // The assistant on the phone (IT: telefono · EN: phone)
      {
        path: 'telefono',
        loadComponent: () => import('./pages/voice.component').then((m) => m.VoicePage),
        data: { seo: 'voice' },
      },
      {
        path: 'phone',
        loadComponent: () => import('./pages/voice.component').then((m) => m.VoicePage),
        data: { seo: 'voice' },
      },

      // Use cases (IT: casi-uso · EN: use-cases) + detail
      {
        path: 'casi-uso',
        loadComponent: () => import('./pages/use-cases.component').then((m) => m.UseCases),
        data: { seo: 'useCases' },
      },
      {
        path: 'use-cases',
        loadComponent: () => import('./pages/use-cases.component').then((m) => m.UseCases),
        data: { seo: 'useCases' },
      },
      // Aziende strutturate is a sheet like the others, but under URLs of its own (the English
      // one is `use-cases/business`): matched BEFORE the `:id` route, which would swallow it.
      {
        path: 'casi-uso/aziende',
        loadComponent: () =>
          import('./pages/feature.main.component').then((m) => m.FeaturesMainComponent),
        data: { seo: 'useCaseBusiness', sector: 'aziende' },
      },
      {
        path: 'use-cases/business',
        loadComponent: () =>
          import('./pages/feature.main.component').then((m) => m.FeaturesMainComponent),
        data: { seo: 'useCaseBusiness', sector: 'aziende' },
      },
      {
        path: 'casi-uso/:id',
        loadComponent: () =>
          import('./pages/feature.main.component').then((m) => m.FeaturesMainComponent),
        data: { seo: 'useCaseDetail' },
      },
      {
        path: 'use-cases/:id',
        loadComponent: () =>
          import('./pages/feature.main.component').then((m) => m.FeaturesMainComponent),
        data: { seo: 'useCaseDetail' },
      },

      // About    (IT: chi-siamo · EN/design: about)
      {
        path: 'about',
        loadComponent: () => import('./pages/about.component').then((m) => m.About),
        data: { seo: 'about' },
      },
      {
        path: 'chi-siamo',
        loadComponent: () => import('./pages/about.component').then((m) => m.About),
        data: { seo: 'about' },
      },

      // Pricing + checkout — omitted in production (subscriptions feature flag).
      ...subscriptionRoutes,

      // Contacts (IT: contatti · EN/spec: contacts)
      {
        path: 'contatti',
        loadComponent: () => import('./pages/contact.component').then((m) => m.Contacts),
        data: { seo: 'contacts' },
      },
      {
        path: 'contacts',
        loadComponent: () => import('./pages/contact.component').then((m) => m.Contacts),
        data: { seo: 'contacts' },
      },

      // Blog — articles come from rlb-chatbot. They are RESOLVED, not fetched inside the
      // component, so SeoService already has the article when it writes the head.
      {
        path: 'blog',
        loadComponent: () => import('./pages/blog.component').then((m) => m.BlogIndex),
        data: { seo: 'blog' },
        resolve: { posts: blogListResolver },
      },
      {
        path: 'blog/:slug',
        loadComponent: () => import('./pages/blog-post.component').then((m) => m.BlogPostPage),
        data: { seo: 'blogPost' },
        resolve: { post: blogPostResolver, posts: blogListResolver },
      },

      // Legal
      {
        path: 'privacy',
        loadComponent: () => import('./pages/privacy.component').then((m) => m.Privacy),
        data: { seo: 'privacy' },
      },
      {
        path: 'termini',
        loadComponent: () => import('./pages/terms.component').then((m) => m.Terms),
        data: { seo: 'terms' },
      },
      {
        path: 'terms',
        loadComponent: () => import('./pages/terms.component').then((m) => m.Terms),
        data: { seo: 'terms' },
      },
      // Delete account — off in production while its text has open fields (feature flag).
      ...deleteAccountRoutes,

      // Dead URL under a valid language. Renders the 404 page IN PLACE — no redirect —
      // so the address the visitor followed stays in the bar and the server can answer
      // 404 instead of 200-with-the-home-page.
      {
        path: '404',
        loadComponent: () => import('./pages/not-found.component').then((m) => m.NotFound),
        data: { seo: 'notFound' },
      },
      {
        path: '**',
        loadComponent: () => import('./pages/not-found.component').then((m) => m.NotFound),
        data: { seo: 'notFound' },
      },
    ],
  },
  // Unreachable in practice — `:lang` matches any single segment — but a catch-all that
  // renders a page is a better last resort than one that redirects.
  {
    path: '**',
    loadComponent: () => import('./pages/not-found.component').then((m) => m.NotFound),
    data: { seo: 'notFound' },
  },
];
